/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Stack } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isNil } from 'lodash';
import { useState } from 'react';
import appTheme from '../common/theme/appTheme.json';

dayjs.extend(utc);

// Limiting start date to 1 year back from current date as data usage is only available for 1 year
const START_DATE_MIN_DATE = dayjs().subtract(1, 'year');
const CURRENT_DATE = dayjs().utc();
export interface DataUsageDatePickerProps {
  onStartDateChanged: (dateString: dayjs.Dayjs) => void;
  onEndDateChanged: (dateString: dayjs.Dayjs) => void;
}
function DataUsageDatePicker({ onStartDateChanged, onEndDateChanged }: DataUsageDatePickerProps) {

  const [startDate, setStartDate] = useState<dayjs.Dayjs>(CURRENT_DATE.subtract(1, 'day'));
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(CURRENT_DATE);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ marginLeft: '21px' }}>
        <DesktopDatePicker
          label="Start Date"
          value={startDate}
          minDate={START_DATE_MIN_DATE}
          maxDate={CURRENT_DATE.subtract(1, 'day')} // Can't start today, need at least 1 day of data
          onChange={newValue => {
            if (!isNil(newValue)) {
              onStartDateChanged(newValue.utc().startOf('day'));
              setStartDate(newValue.utc().startOf('day'));

              // Ensure end data always stays after start date 
              if (endDate.isBefore(newValue) || endDate.isSame(newValue)) {
                onEndDateChanged(newValue.utc().add(1, 'day'));
                setEndDate(newValue.utc().add(1, 'day'));
              }
            }
          }}
          slotProps={datePickerSlotProps}
        />
        <DesktopDatePicker
          sx={{ width: '240px' }}
          label="End Date"
          value={endDate}
          minDate={startDate.add(1, 'day')}
          maxDate={CURRENT_DATE}
          onChange={newValue => {
            if (!isNil(newValue)) {
              onEndDateChanged(newValue.utc().startOf('day'));
              setEndDate(newValue.utc().startOf('day'));
            }
          }}
          slotProps={datePickerSlotProps}
        />
      </Stack>
    </LocalizationProvider>
  );
}

export default DataUsageDatePicker;

const datePickerSlotProps = {
  textField: {
    sx: {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#9FAFBC',
        },
        '&:hover fieldset': {
          borderColor: 'white',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00C2DE',
        },
      },
      input: { color: appTheme.primaryTextColor, height: '8px', width: '160px' },
      fieldset: {
        borderColor: appTheme.primaryTextColor,
      },
    },
  },

  popper: {
    sx: {
      '.MuiDayCalendar-weekDayLabel': {
        color: appTheme.secondaryTextColor,
      },
      '.MuiPickersDay-root': {
        color: appTheme.primaryTextColor,
        backgroundColor: appTheme.primaryBackgroundColor,
        '&:hover': {
          backgroundColor: `${appTheme.mptSelectIconGray}`,
          color: appTheme.primaryTextColor,
        },
      },
      '.Mui-selected': {
        backgroundColor: `${appTheme.mptLightBlue} !important`,
        color: appTheme.primaryBackgroundColor,
        '&:hover': {
          backgroundColor: `${appTheme.mptSelectIconGray} !important`,
          color: `${appTheme.primaryTextColor} !important`,
        },
      },
    },
  },
};
